import React, { useContext, useEffect, useMemo } from "react";
import { MY_BOTS_LOADING_ID } from "testIds";
import BotsStatistics from "features/bots/MyBots/BotsStatistics";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import BotsCards from "features/bots/MyBots/BotsCards";
import BotsTable from "features/bots/MyBots/BotsTable";
import useSocketAggBotData from "@shared/services/socket/useSocketAggBotData";
import {
  useBottomNavigator,
  useHeaderSearchValue,
  useOnReconnect,
} from "utils/hooks/misc";
import {
  BOT_TYPE_ANALYTICS_MAPPING,
  BOTS_PAGE_LAYOUT_CHANGE_KEY,
} from "features/bots/common/constants";
import BotsEmpty from "features/bots/common/BotsEmpty";
import { BotsBulkActionsContext } from "@shared/features/bots/BotsBulkActionsProvider";
import localStorageService from "utils/localStorageService";
import { PageLayout } from "types";
import CardsLayoutProvider from "features/bots/BotForm/common/CardsLayoutProvider";
import { MyBotsPageContext } from "@shared/features/bots/MyBotsPage.context";
import { PageTabsContext } from "common/Tabs/PageTabs.context.ts";
import { MobileBotsTab } from "@shared/features/bots/types.ts";
import { BotType } from "@shared/types";
import TradingViewBotsBanner from "features/bots/MyBots/TradingViewBotsBanner.tsx";
import { useSelectUser } from "@shared/utils/hooks/misc.ts";
import { useTheme } from "styled-components";
import NewLookBotsPerformance from "features/bots/NewLook/NewLookBotsPerformance";
import NewLookBotsListHeader from "features/bots/NewLook/NewLookBotsListHeader";
import { NewLookBotsToolbarContext } from "features/bots/NewLook/NewLookBotsToolbar.context";
import PageIntroActions from "features/bots/NewLook/PageIntroActions";
import NewLookMyBotsSkeleton from "features/bots/NewLook/NewLookMyBotsSkeleton";

interface MyBotsProps {
  isCardsView: boolean;
  searchValue?: string;
  setLoading: (loading: boolean) => void;
  setShowFilters: (showFilters: boolean) => void;
  setIsCardsView: (isCardsView: boolean) => void;
}

const MyBots = ({
  isCardsView,
  searchValue,
  setLoading,
  setShowFilters,
  setIsCardsView,
}: MyBotsProps) => {
  const { analytics } = useContext(AnalyticsContext);
  const { botType, isChannelConfiguration, getBotsDataStatus, bots } =
    useContext(MyBotsPageContext)!;
  const { isBulkMode } = useContext(BotsBulkActionsContext)!;
  const { setTabCount } = useContext(PageTabsContext) || {};
  const { data: botsData, isFetching, refetch } = getBotsDataStatus;
  const headerSearch = useHeaderSearchValue(searchValue);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const user = useSelectUser();
  const { isNewLook } = useTheme();
  const isNewLookDesktop = isNewLook && !isBottomNavigator;
  const newLookToolbar = useContext(NewLookBotsToolbarContext);

  const socketArgs = useMemo(
    () => ({ fetch_initial_data: !!botsData?.data }),
    []
  );
  useSocketAggBotData(socketArgs, refetch);
  useOnReconnect(refetch);

  const isLoading =
    !getBotsDataStatus.currentData || getBotsDataStatus.isLoading;
  const tabName =
    botType === BotType.TRADING_VIEW_SIGNALS
      ? MobileBotsTab.TV_TO_SIGNALS
      : MobileBotsTab.MY_BOTS;

  useEffect(() => {
    setTabCount?.(tabName, isLoading ? undefined : bots.length);
  }, [bots, isLoading, tabName]);

  useEffect(() => {
    setLoading(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (
      botsData?.data?.length &&
      botsData?.data?.length > 50 &&
      !localStorageService.getParsedValue<PageLayout>(
        BOTS_PAGE_LAYOUT_CHANGE_KEY
      )
    ) {
      setIsCardsView(false);
    }
  }, [botsData]);

  useEffect(() => {
    if (isBottomNavigator) setIsCardsView(true);
  }, [isBottomNavigator]);

  useEffect(() => {
    analytics?.botMyBotsViewWeb({
      botType: BOT_TYPE_ANALYTICS_MAPPING[botType],
    });
  }, [analytics, botType]);

  const hasData = !!botsData?.data.length;
  const isSearch = !!headerSearch?.length;
  const emptyState = useMemo(
    () => (
      <BotsEmpty
        botType={botType}
        hasData={hasData}
        isSearch={isSearch}
        openFilters={() => setShowFilters(true)}
        isChannelConfiguration={isChannelConfiguration}
      />
    ),
    [botType, isChannelConfiguration, hasData, isSearch]
  );

  const getBots = () => {
    if (isCardsView) {
      return (
        <CardsLayoutProvider>
          <BotsCards emptyState={emptyState} />
        </CardsLayoutProvider>
      );
    } else {
      return <BotsTable emptyState={emptyState} />;
    }
  };

  const showStatistics =
    !isBottomNavigator &&
    !isBulkMode &&
    !isChannelConfiguration &&
    botType !== BotType.TRADING_VIEW_SIGNALS;

  if (
    isNewLook &&
    !getBotsDataStatus.currentData &&
    !getBotsDataStatus.isUninitialized &&
    !getBotsDataStatus.isError
  ) {
    // The page's own shape in placeholders (no spinner over an empty box).
    return (
      <div data-testid={MY_BOTS_LOADING_ID} aria-busy="true">
        <NewLookMyBotsSkeleton
          showPerformance={
            !isBulkMode &&
            !isChannelConfiguration &&
            botType !== BotType.TRADING_VIEW_SIGNALS
          }
          isCardsView={isCardsView || isBottomNavigator}
          compact={isBottomNavigator}
        />
      </div>
    );
  }

  if (isNewLookDesktop) {
    // Without any bots the empty state is the page: no zeroed figures and no
    // list controls above it (unless filters or search hide them).
    const showListChrome = hasData || !!newLookToolbar?.isFiltering;
    return (
      <>
        {botType === BotType.TRADING_VIEW && user?.has_old_tradingview_bots && (
          <TradingViewBotsBanner />
        )}
        {showStatistics && showListChrome && (
          <div style={{ marginBottom: isCardsView ? 24 : 16 }}>
            <NewLookBotsPerformance botType={botType} isLoading={isLoading} />
          </div>
        )}
        {!showListChrome && newLookToolbar && (
          <PageIntroActions
            actions={newLookToolbar.quietPageActions}
            deps={newLookToolbar.pageActionsDeps}
          />
        )}
        {showListChrome && !isCardsView ? (
          // Table: the list header is the header of the table's panel.
          <NewLookBotsListHeader
            isCardsView={isCardsView}
            setIsCardsView={setIsCardsView}
            count={isLoading ? undefined : bots.length}
          >
            {getBots()}
          </NewLookBotsListHeader>
        ) : (
          <>
            {showListChrome && (
              <NewLookBotsListHeader
                isCardsView={isCardsView}
                setIsCardsView={setIsCardsView}
                count={isLoading ? undefined : bots.length}
              />
            )}
            <div style={{ position: "relative" }}>{getBots()}</div>
          </>
        )}
      </>
    );
  }

  return (
    <>
      {botType === BotType.TRADING_VIEW && user?.has_old_tradingview_bots && (
        <TradingViewBotsBanner />
      )}
      {showStatistics && (
        <div style={{ marginBottom: 16 }}>
          <BotsStatistics botType={botType} isLoading={isLoading} />
        </div>
      )}
      <div style={{ position: "relative", height: "100%" }}>{getBots()}</div>
    </>
  );
};

export default MyBots;
